import { useCallback, useEffect, useState } from 'react';
import { authService } from '../services/auth';
import { ApiError } from '../services/api';
import { track } from '../services/events';

export function useAuthFlow(
  onLogin: (input: {
    challenge_id: string;
    mobile: string;
    company_name?: string;
    code: string;
  }) => Promise<void>
) {
  const [mobile, setMobile] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [code, setCode] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const [challengeId, setChallengeId] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [sending, setSending] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [captchaRequired, setCaptchaRequired] = useState(false);
  useEffect(() => {
    if (!countdown) return;
    const timer = window.setInterval(() => setCountdown((v) => Math.max(0, v - 1)), 1000);
    return () => clearInterval(timer);
  }, [countdown]);
  const send = useCallback(async () => {
    if (!/^1\d{10}$/.test(mobile)) {
      setError('请输入 11 位中国大陆手机号');
      return;
    }
    setSending(true);
    setError('');
    track('sms_send_click');
    try {
      const result = await authService.sendCode({ mobile, captcha_token: captchaToken });
      setChallengeId(result.challenge_id);
      setCountdown(result.resend_after_seconds || 60);
      setCaptchaRequired(false);
      track('sms_send_success');
    } catch (e) {
      if (e instanceof ApiError && e.error === 'CaptchaRequired') setCaptchaRequired(true);
      setError(e instanceof Error ? e.message : '验证码发送失败');
    } finally {
      setSending(false);
    }
  }, [mobile, captchaToken]);
  const submit = useCallback(async () => {
    if (!challengeId) {
      setError('请先获取验证码');
      return false;
    }
    if (!/^\d{4,6}$/.test(code)) {
      setError('请输入 4-6 位数字验证码');
      return false;
    }
    if (companyName && (companyName.trim().length < 2 || companyName.trim().length > 50)) {
      setError('企业名称需为 2-50 个字符');
      return false;
    }
    setSubmitting(true);
    setError('');
    try {
      await onLogin({
        challenge_id: challengeId,
        mobile,
        company_name: companyName.trim() || undefined,
        code
      });
      track('login_success');
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : '登录失败');
      return false;
    } finally {
      setSubmitting(false);
    }
  }, [challengeId, code, companyName, mobile, onLogin]);
  return {
    mobile,
    setMobile,
    companyName,
    setCompanyName,
    code,
    setCode,
    captchaToken,
    setCaptchaToken,
    challengeId,
    countdown,
    sending,
    submitting,
    error,
    captchaRequired,
    send,
    submit
  };
}
